import { FC, useEffect, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import {
  Question,
  Submission,
  QuestionOption,
  ExerciseOptionTypes,
} from "@/types";
import Button from "@edusogno/primitives/button.js";
import InfoIcon from "@/components/icons/exercise/InfoIcon";
import CheckIcon from "@/components/icons/exercise/CheckIcon";
import TrueOrFalseIcone from "@/components/icons/exerciseV2/TrueOrFalseIcon";
import MultipleChoiceIcon from "@/components/icons/exerciseV2/MultipleChoiceIcon";
import GrammarExerciseResult from "./GrammarExerciseResult";

interface QuestionOptionProps {
  id: number;
  text: string;
  selected: boolean;
  onSelect: (id: number) => void;
  explanation?: string;
  isCorrectOption: boolean;
  highlightCorrect: boolean;
  isCorrectlySelected: boolean | null;
}

const QuestionOptions: FC<QuestionOptionProps> = ({
  id,
  text,
  selected,
  onSelect,
  explanation,
  isCorrectOption,
  highlightCorrect,
  isCorrectlySelected,
}) => {
  let textColor, borderColor, iconBgColor, bgColor, Icon;

  if (selected) {
    if (isCorrectlySelected === false) {
      borderColor = "border-[#FE9595]";
      iconBgColor = "bg-black";
      bgColor = "bg-[#FE9595]";
      Icon = CheckIcon;
    } else if (isCorrectlySelected) {
      borderColor = "border-[#D3EADD]";
      iconBgColor = "bg-black";
      Icon = CheckIcon;
      bgColor = "bg-[#D3EADD]";
    }
  } else if (isCorrectOption && highlightCorrect) {
    bgColor = "bg-[#D3EADD]";
    borderColor = "border-[#D3EADD]";
    iconBgColor = "bg-light-400";
    textColor = "text-black";

    Icon = null;
  } else {
    borderColor = "border-brand-transparent";
    iconBgColor = "bg-light-400";
    bgColor = "bg-light-400";
    Icon = null;
  }

  return (
    <div
      className={`flex gap-5 justify-between px-5 py-2.5 mt-1.5 text-sm font-normal leading-4 rounded-md border max-md:flex-wrap max-md:max-w-full text-brand-DEFAULT ${textColor} ${bgColor} ${borderColor}`}
      onClick={() => onSelect(id)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") onSelect(id);
      }}
      role="button"
      tabIndex={0}
    >
      <div
        className={`w-5 h-5 rounded-md border border-solid border-black flex justify-center items-center ${iconBgColor}`}
      >
        {Icon && <Icon />}
      </div>

      <div className="grow text-[10px] font-medium">{text}</div>

      {isCorrectlySelected === false && explanation && (
        <Popover>
          <PopoverTrigger asChild>
            <button className="ml-4">
              <InfoIcon />
            </button>
          </PopoverTrigger>

          <PopoverContent
            align="end"
            side="left"
            className="border border-[#C80505]"
            sideOffset={10}
          >
            <p className="text-sm text-brand">{explanation}</p>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

interface QuestionProgressBarProps {
  currentQuestion: number;
  totalQuestions: number;
}

const QuestionProgressBar: FC<QuestionProgressBarProps> = ({
  currentQuestion,
  totalQuestions,
}) => {
  const progressPercentage = ((currentQuestion / totalQuestions) * 100).toFixed(
    0,
  );

  return (
    <div className="flex flex-row items-center self-start my-2 w-full">
      <div className="flex flex-col justify-center mr-2 rounded-sm bg-[#D9D9D9] w-full">
        <div
          className="shrink-0 h-1 bg-black rounded-sm"
          style={{ width: `${progressPercentage}%` }}
        />
      </div>

      <div className="text-[10px] font-semibold leading-3 text-black whitespace-nowrap">
        {currentQuestion}-{totalQuestions}
      </div>
    </div>
  );
};

interface MultipleChoiceExerciseProps {
  questions: Question[];
  exerciseType:
    | ExerciseOptionTypes.MultipleChoice
    | ExerciseOptionTypes.TrueFalse;
}

const MultipleChoiceExerciseMobile: FC<MultipleChoiceExerciseProps> = ({
  questions,
  exerciseType,
}) => {
  const totalQuestions = questions.length;
  const multipleChoice = exerciseType === ExerciseOptionTypes.MultipleChoice;
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const currentQuestion = questions[currentQuestionIndex];
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [responseSelected, setResponseSelected] = useState(false);
  const [isAnswerCorrect, setIsAnswerCorrect] = useState<boolean | null>(null);
  const [highlightCorrect, setHighlightCorrect] = useState(false);
  const [submissionData, setSubmissionData] = useState<Submission[]>([]);
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const [showResults, setShowResults] = useState(false);
  const [correctAnswersCount, setCorrectAnswersCount] = useState(0);

  const options = currentQuestion?.options?.map((option: QuestionOption) => ({
    id: option.id,
    text: option.option,
    isCorrect: option.is_correct === 1,
    explanation: option.explanation || "",
  }));

  const handleSelect = (selectedId: number) => {
    if (responseSelected) return;

    const selected = options.find((option) => option.id === selectedId);
    if (!selected) return;

    setResponseSelected(true);
    setSelectedOption(selected.id);
    setIsAnswerCorrect(selected.isCorrect);
    setHighlightCorrect(!selected.isCorrect);
    const isCorrect = selected.isCorrect;

    if (isCorrect) {
      setCorrectAnswersCount(correctAnswersCount + 1);
    }

    const questionId = currentQuestion.id;
    const newSubmission: Submission = {
      questionId,
      selectedOptionId: selected.id,
      isCorrect: selected.isCorrect,
    };

    setSubmissionData([...submissionData, newSubmission]);
  };

  const handleNextQuestion = () => {
    setResponseSelected(false);
    setSelectedOption(null);
    setIsAnswerCorrect(null);
    setHighlightCorrect(false);
    setCurrentQuestionIndex(currentQuestionIndex + 1);
  };

  const getOptionLetter = (index: number) => {
    // ASCII code for 'A' is 65
    return String.fromCharCode(65 + index);
  };

  useEffect(() => {
    // Blocca lo scroll
    document.body.style.overflowY = "hidden";

    // Riabilita lo scroll quando il componente viene smontato
    return () => {
      document.body.style.overflowY = "auto";
    };
  }, []);

  const [scoreForFinal, setScoreForFinal] = useState<number>(0);

  const handleShowResults = () => {
    const score = correctAnswersCount;

    setShowResults(true);
    setCurrentQuestionIndex(0);
    setSelectedOption(null);
    setResponseSelected(false);
    setIsAnswerCorrect(null);
    setHighlightCorrect(false);
    setSubmissionData([]);
    setScoreForFinal(score);
    setCorrectAnswersCount(0);
  };

  if (showResults) {
    return (
      <GrammarExerciseResult
        onClose={() => setShowResults(false)}
        data={{
          exerciseType: exerciseType,
          score: scoreForFinal,
          total: totalQuestions,
          submissionData: submissionData,
        }}
      />
    );
  }

  return (
    <>
      <div className="flex flex-col gap-2 px-5 pt-5 bg-white rounded-lg border border-brand-secondary border-solid">
        <header className="flex gap-5 justify-between w-full">
          <div className="flex gap-2 justify-between items-center text-xl leading-3 text-black">
            {multipleChoice ? (
              <MultipleChoiceIcon className="md:w-4 md:h-4" />
            ) : (
              <TrueOrFalseIcone className="md:w-4 md:h-4" />
            )}

            <div className="w-full">
              <p className="text-xs font-bold ">
                {multipleChoice ? "Multiple choice" : "True or False"}
              </p>

              <p className="text-[10px] font-normal text-black">
                Choose the correct sentence
              </p>
            </div>
          </div>
        </header>
        <div>
          <p className="text-xs font-normal  text-black">
            {currentQuestion?.title ? currentQuestion?.title : ""}
          </p>
        </div>
        <div>
          <div className="text-[10px] font-semibold leading-4 text-black">
            {currentQuestionIndex + 1} - {currentQuestion.question}
          </div>

          {options.map((option, index) => (
            <QuestionOptions
              id={option.id}
              key={index}
              text={`${getOptionLetter(index)} - ${option.text}`}
              onSelect={() => handleSelect(option.id)}
              selected={selectedOption === option.id}
              isCorrectOption={option.isCorrect}
              isCorrectlySelected={
                selectedOption === option.id ? isAnswerCorrect : null
              }
              highlightCorrect={
                highlightCorrect && !isAnswerCorrect && option.isCorrect
              }
              explanation={option.explanation}
            />
          ))}
        </div>

        <QuestionProgressBar
          currentQuestion={currentQuestionIndex + 1}
          totalQuestions={totalQuestions}
        />
      </div>

      <Button
        variant="contained"
        disabled={!responseSelected}
        className={`mx-auto mt-4 text-[10px] w-full rounded-[10px] ${
          !responseSelected ? "bg-white hover:bg-white text-black" : ""
        } ${
          isLastQuestion
            ? "bg-brand-secondary hover:bg-brand-secondary text-white"
            : ""
        } `}
        onClick={isLastQuestion ? handleShowResults : handleNextQuestion}
      >
        {isLastQuestion ? "My Score ⭐" : "Next question"}
      </Button>
    </>
  );
};

export default MultipleChoiceExerciseMobile;
